// src/components/Tutorial.jsx
import { useState, useEffect, useCallback } from "react";
import PinchIcon from "./PinchIcon";

const STEPS_USER = [
  {
    target: null,
    view: null,
    brand: true,
    title: "Welcome to Pinch Point!",
    body: "This quick tour will show you the key features. You can exit any time and relaunch from your profile menu.",
  },
  {
    target: "[data-tut='post-form-box']",
    view: "today",
    title: "Submitting a Post",
    body: "Describe an issue and the AI will suggest a category and severity — accept or override them. Tap \"View Guidelines\" for posting standards, or use the AI rewrite option to polish your description.",
    mobileTarget: "[data-tut='fab']",
  },
  {
    target: "[data-tut='board-header']",
    view: "today",
    title: "Live Logs",
    body: "All open posts appear here in real time, organized by severity: Critical, Moderate, and Low. Upvote a post to signal urgency. Admins see additional moderation options.",
  },
  {
    target: "[data-tut='nav-mytasks']",
    view: null,
    title: "Tasks",
    body: "Posts can be converted into tasks and assigned to team members. You'll receive a notification when a task is assigned to you.",
  },
  {
    target: "[data-tut='nav-dashboard']",
    view: "dashboard",
    title: "Dashboard",
    body: "Track trends with charts: bar, pie, lollipop, bump, and heat map. Use date and category filters to drill down, then download reports as PDF or CSV.",
  },
];

const ADMIN_STEPS = [
  {
    target: "[data-tut='nav-admin']",
    view: "admin",
    title: "Admin Features",
    body: "As an admin you have access to user management, reported posts, systemic AI alerts, the guidelines editor, and report generation.",
  },
];

const FINAL_STEP = [
  {
    target: "[data-tut='profile-btn']",
    view: null,
    title: "You're All Set!",
    body: "You can relaunch this tutorial anytime from your profile menu.",
  },
];

const PAD = 8;
const TOOLTIP_W = 300;
const TOOLTIP_H_EST = 220; // generous height estimate for positioning

export default function Tutorial({ isAdmin, onDismiss, onSetView, currentView }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [windowSize, setWindowSize] = useState({ w: window.innerWidth, h: window.innerHeight });

  const allSteps = isAdmin ? [...STEPS_USER, ...ADMIN_STEPS, ...FINAL_STEP] : [...STEPS_USER, ...FINAL_STEP];
  const total = allSteps.length;
  const cur = allSteps[step];
  const isMobile = windowSize.w <= 960;

  const updateRect = useCallback(() => {
    const sel = (isMobile && cur.mobileTarget) ? cur.mobileTarget : cur.target;
    if (!sel) { setRect(null); return; }
    const el = document.querySelector(sel);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    // Small delay after scroll to let layout settle
    requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) { setRect(null); return; }
      setRect({ left: r.left, top: r.top, width: r.width, height: r.height });
    });
  }, [cur, isMobile]);

  useEffect(() => {
    if (cur.view && cur.view !== currentView) onSetView(cur.view);
    const t = setTimeout(updateRect, 280);
    return () => clearTimeout(t);
  }, [step]); // eslint-disable-line

  useEffect(() => {
    const onResize = () => {
      setWindowSize({ w: window.innerWidth, h: window.innerHeight });
      updateRect();
    };
    const onScroll = () => updateRect();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [updateRect]);

  const goNext = () => { if (step < total - 1) setStep((s) => s + 1); else onDismiss(); };
  const goBack = () => { if (step > 0) setStep((s) => s - 1); };

  const vw = windowSize.w;
  const vh = windowSize.h;
  const tooltipW = Math.min(TOOLTIP_W, vw - 24);

  // Spotlight style — uses box-shadow to dim everything except the target element
  const spotStyle = rect ? {
    position: "fixed",
    left: rect.left - PAD,
    top: rect.top - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
    borderRadius: 8,
    boxShadow: "0 0 0 9999px rgba(0,0,0,0.65)",
    border: "2px solid rgba(255,191,39,0.7)",
    zIndex: 1001,
    pointerEvents: "none",
    transition: "left 0.25s ease, top 0.25s ease, width 0.25s ease, height 0.25s ease",
  } : null;

  // Tooltip positioning — always stays within viewport bounds
  const getTooltipStyle = () => {
    // Horizontal: align left with element edge, clamped inside viewport
    let left;
    if (!rect) {
      left = Math.max(12, Math.round((vw - tooltipW) / 2));
    } else {
      left = rect.left;
      if (left + tooltipW > vw - 12) left = vw - tooltipW - 12;
      if (left < 12) left = 12;
    }

    // Vertical: pick the side with more room, then clamp
    let top;
    if (!rect) {
      top = Math.max(12, Math.round((vh - TOOLTIP_H_EST) / 2));
    } else {
      const elemBottom = rect.top + rect.height;
      const spaceBelow = vh - (elemBottom + PAD + 14);
      const spaceAbove = rect.top - PAD - 14;

      if (spaceBelow >= TOOLTIP_H_EST || spaceBelow >= spaceAbove) {
        // Place below — clamp so bottom edge stays in viewport
        top = Math.min(elemBottom + PAD + 14, vh - TOOLTIP_H_EST - 12);
      } else {
        // Place above — clamp so top edge stays in viewport
        top = Math.max(rect.top - PAD - 14 - TOOLTIP_H_EST, 12);
      }
    }
    // Hard clamp: never fully off-screen
    top = Math.max(12, Math.min(top, vh - TOOLTIP_H_EST - 12));

    return {
      position: "fixed",
      top,
      left,
      width: tooltipW,
      zIndex: 1002,
      maxHeight: vh - top - 12,
      overflowY: "auto",
    };
  };

  return (
    <>
      {/* Full-screen click blocker — transparent so app shows through spotlight */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1000,
          background: spotStyle ? "transparent" : "rgba(0,0,0,0.65)",
          pointerEvents: "all",
        }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Spotlight */}
      {spotStyle && <div style={spotStyle} />}

      {/* Tooltip */}
      <div className="tut-tooltip" style={getTooltipStyle()}>
        <div className="tut-progress">
          {allSteps.map((_, i) => (
            <div key={i} className={`tut-pip${i === step ? " active" : i < step ? " done" : ""}`} />
          ))}
        </div>
        {cur.brand && (
          <div className="tut-brand">
            <span className="tut-brand-name">Pinch Point</span>
            <PinchIcon />
          </div>
        )}
        <div className="tut-title">{cur.title}</div>
        <div className="tut-body">{cur.body}</div>
        <div className="tut-footer">
          <button className="tut-skip-btn" onClick={onDismiss}>Exit Tour</button>
          <div className="tut-nav-btns">
            {step > 0 && (
              <button className="tut-back-btn" onClick={goBack}>← Back</button>
            )}
            <button className="tut-next-btn" onClick={goNext}>
              {step === total - 1 ? "Finish ✓" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
